/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import moment from 'moment';
import { isEqual, cloneDeep } from 'lodash';
import Default from '../../constants/defaults';
import DetailsHeader from './cadenceDetailsHeader';
import CadencePlanTab from './cadencePlanTab';
import CadenceStoriesTab from './cadenceStoriesTab';
import CadenceTasksTab from './cadenceTasksTab';
import CadenceMetricsTab from './cadenceMetricsTab';
import { vrsSummation } from '../../utils/objectHelpers';
import { validEndDate, adjustDates } from '../../utils/sprintHelpers';
import { fetchUsers } from '../../actions/userActions';
import { openModal } from '../../actions/modalActions';
import {
  fetchTargetWorkPackages,
  addWorkPackageToCadence,
  removeWorkPackageFromCadence
} from '../../actions/workPackageActions';
import {
  updateCadence,
  getAllCadences,
  deleteCadence,
  restoreCadence,
  createCadence
} from '../../actions/cadenceActions';
import CadenceDetailStatus from './cadenceDetailsStatus';
import { cadenceWorkPackagesSelector } from '../../selectors/workPackageSelector';
import { allows } from '../../utils/authorization';
import divider from '../../../assets/images/large-card-nav-divider.png';
import { currentOrganization } from '../../utils/organizationHelpers';
import { CadenceStatuses } from '../../constants/storyStatusTypes';

class CadenceDetail extends React.Component {
  constructor(props) {
    super(props);
    const {
      cadence = {},
      cadenceList,
      currentTeams,
      scrumTeams,
      organizationLevels,
      modalState,
    } = props;
    const teamId =
      currentTeams.length > 0 ? currentTeams[0].id : scrumTeams[0].id;
    const teamCadences = cadenceList.filter(
      (cadenceItem) => cadenceItem.team_id === teamId && !cadenceItem.deleted_at
    );
    const plannedCadences = teamCadences.filter(
      (c) => c.status === CadenceStatuses.GETTING_READY
    );
    const isStartable =
      cadence.status === CadenceStatuses.GETTING_READY &&
      cadence.id === plannedCadences[0]?.id;
    // ensure only non-overlapping deleted cadences can be restored
    const isRestorable =
      cadence.deleted_at &&
      teamCadences.find(
        (c) =>
          (moment(cadence.start_date).isAfter(c.start_date) &&
            moment(cadence.start_date).isBefore(c.end_date)) ||
          (moment(cadence.end_date).isAfter(c.start_date) &&
            moment(cadence.end_date).isBefore(c.end_date))
      ) === undefined;
    const lastPlanned = teamCadences[teamCadences.length - 1];
    const previousEndDate = lastPlanned && moment(lastPlanned.end_date);
    const likelyStartDate = moment.max(previousEndDate, moment()) || moment();
    const likelyEndDate =
      lastPlanned &&
      moment(likelyStartDate).add(lastPlanned.interval || 7, 'days');
    const sprintId = lastPlanned ? lastPlanned.sprint_id + 1 : 1;

    let organizationLevel = organizationLevels.find(
      (ol) => ol.default_level && ol.level === 1
    );

    if (!organizationLevel) {
      organizationLevel = organizationLevels.find((ol) => ol.level === 1);
    }

    this.state = {
      dropDown: {},
      titleEdited: false,
      hasChanged: false,
      // dateEmpty: false,
      disableBtn: false,
      isFormValid: true,
      editGoal: false,
      activeTab: 'plan',
      isStartable,
      isRestorable,
      cadence: modalState.cadence || {
        // use cadence from modalState if available
        ...cadence,
        id: cadence.id || 0,
        short_name: cadence.short_name || `Sprint ${sprintId}`,
        team_id: cadence.team_id || teamId,
        start_date: cadence.start_date || likelyStartDate,
        end_date: cadence.end_date || likelyEndDate,
        workPackageIds: [],
        goal: cadence.goal ? cadence.goal : ''
      },
      organizationLevel
    };
  }

  componentDidUpdate(prevProps) {
    const { cadence } = this.props;
    if (cadence && prevProps.cadence && !isEqual(prevProps.cadence, cadence)) {
      const newCadence = { ...cadence };

      this.setState({ cadence: newCadence });
    }
  }

  setDropdown = (updatedDropdDown) => {
    this.setState({ dropDown: updatedDropdDown });
  };

  handleFieldChange = (field) => {
    const newCadence = { ...this.state.cadence };
    if (field.end_date) newCadence.end_date = field.end_date;
    else if (field.start_date) newCadence.start_date = field.start_date;
    else if (field.goal) newCadence.goal = field.goal;
    else newCadence.short_name = field.short_name;
    if (newCadence.id && field.end_date) {
      newCadence.end_date = field.end_date;
      if (!validEndDate(newCadence)) {
        adjustDates(
          this.props.cadence,
          this.props.cadence.status,
          field.end_date
        );
        return;
      }
    }
    this.setState({ cadence: newCadence, hasChanged: !newCadence.id }, () => {
      const { cadence } = this.state;
      if (cadence.id) {
        if (!cadence.short_name || !cadence.end_date || !cadence.start_date) {
          this.setState({ isFormValid: false });
        } else {
          const value = field.target
            ? { [field.target.name]: field.target.value }
            : field;
          this.props.updateCadence(this.props.cadence.id, { cadence: value });
        }
      } else this.setState({ isFormValid: true });
    });
  };

  handleSelectChange = (preValue, currValue, name) => {
    if (preValue === currValue) return;
    const { cadence } = this.state;
    cadence[name] = currValue;
    this.setState({ cadence });
    if (cadence.id)
      this.props.updateCadence(cadence.id, {
        cadence: { [name]: currValue }
      });
  };

  handleUnprocessable = (errors) => {
    this.setState({ isFormValid: false, errors });
  };

  handleSave = (event) => {
    if (event) event.preventDefault();
    const { cadence } = this.state;
    this.setState({ titleEdited: true });
    if (!cadence.short_name || !cadence.end_date || !cadence.start_date) {
      return this.setState({ isFormValid: false });
    }
    this.setState({ disableBtn: true });
    this.props.createCadence(cadence, this.handleUnprocessable).then(() => {
      if (this.state.isFormValid) this.props.onClose();
    });
    return null;
  };

  handleAddWorkPackageToCadence = (wpId) => {
    const { cadence } = this.state;
    if (cadence.id) return this.props.addWorkPackageToCadence(wpId, cadence.id);
    const newCadence = cloneDeep(cadence);
    newCadence.workPackageIds.push(wpId);
    this.setState({ cadence: newCadence });
    this.props.saveModalState({ cadence: newCadence }); // preserve against unmounting when child modal opened
    return 'Added';
  };

  handleRemoveWorkPacakgeFromCadence = (wpId) => {
    const { cadence } = this.state;
    if (cadence.id) this.props.removeWorkPackageFromCadence(wpId, cadence.id);
    else {
      const newCadence = cloneDeep(cadence);
      newCadence.workPackageIds = newCadence.workPackageIds.filter(
        (id) => id !== wpId
      );
      this.setState({ cadence: newCadence });
      this.props.saveModalState({ cadence: newCadence }); // preserve against unmounting when child modal opened
    }
  };

  closeForm = () => {
    // const { hasChanged } = this.state;

    // if (hasChanged) {
    //   const message =
    //     'Do you want to save the changes you made to this Sprint?';
    //   const actions = {
    //     save: {
    //       name: 'Save',
    //       function: this.handleSave
    //     },
    //     dontSave: {
    //       name: "Don't save",
    //       function: this.props.onClose
    //     }
    //   };
    //   this.props.openModal(
    //     modalTypes.message,
    //     {
    //       message,
    //       actions,
    //       closeButtonTitle: 'Cancel'
    //     },
    //     'confirm'
    //   );
    // } else {
    this.props.onClose();
    // }
  };

  renderTabLink = (name) => {
    const { cadence } = this.state;
    if (cadence.id) {
      return (
        <a href={`#${name}`} onClick={() => this.setActiveTab(name)}>
          {name.toUpperCase()}
        </a>
      );
    }
    return <a>{name.toUpperCase()}</a>;
  };

  handleGoalChange = (e) => {
    const newValue = e.target.value;

    this.setState((prevState) => ({
      ...prevState,
      cadence: {
        ...prevState.cadence,
        goal: newValue
      }
    }));
  };

  openGoalEdit = (authorization) => {
    if (this.state.editGoal) return;
    this.setState({ editGoal: true });
  };

  closeGoalUpdate = () => {
    this.setState({ editGoal: false });
    if (!this.props.cadence || !this.props.cadence.id) return;

    this.props.updateCadence(this.props.cadence.id, {
      cadence: { goal: this.state.cadence.goal }
    });
    // this.props.updateCadence(this.props.cadence.id, { cadence: value })
  };

  renderGoalEditing = () => {
    const { editGoal, cadence } = this.state;

    if (editGoal) {
      return (
        <input
          type="text"
          autoFocus={editGoal}
          value={cadence.goal}
          onChange={this.handleGoalChange}
          placeholder="Input a Sprint Goal"
          onBlur={this.closeGoalUpdate}
        />
      );
    }
    if (cadence.goal) {
      return `${cadence.goal}`;
    }
    return <span className="text-placeholder">Input a Sprint Goal</span>;
  };

  renderActiveTab = (
    dropDown,
    resultIds,
    results,
    vrsTotal,
    cadence,
    currentUser,
    allowEdit,
    organizationLevel,
    tableFields
  ) => {
    switch (this.state.activeTab) {
      case 'plan':
        return (
          <CadencePlanTab
            dropDown={dropDown}
            resultIds={resultIds}
            results={results}
            vrsTotal={vrsTotal}
            cadence={cadence}
            currentUser={currentUser}
            allowEdit={allowEdit}
            organizationLevel={organizationLevel}
            handleAddWorkPackageToCadence={this.handleAddWorkPackageToCadence}
            handleRemoveWorkPacakgeFromCadence={
              this.handleRemoveWorkPacakgeFromCadence
            }
            setDropdown={this.setDropdown}
          />
        );

      case 'scope':
        return (
          <CadenceStoriesTab
            results={results}
            tableFields={tableFields}
            dropDown={dropDown}
            cadence={cadence}
            currentUser={currentUser}
            allowEdit={allowEdit}
            organizationLevel={organizationLevel}
            setDropdown={this.setDropdown}
          />
        );

      case 'tasks':
        return <CadenceTasksTab />;

      case 'metrics':
        return <CadenceMetricsTab />;

      default:
    }
  };

  setActiveTab = (tabName) => {
    this.setState({ activeTab: tabName });
  };

  generateTabClass = (tabName) =>
    tabName === this.state.activeTab ? 'active' : '';

  generateVrsTotal = () => {
    const { cadence } = this.state;

    if (cadence.id === 0) {
      const { workPackageIds } = cadence;
      const { resultList } = this.props;

      const results = resultList.filter((r) => workPackageIds.includes(r.id));
      return vrsSummation(results);
    }
    return vrsSummation(this.props.results);
  };

  render() {
    const { isOpen, currentUser, allCadences, resultList } = this.props;
    let { results } = this.props;
    const {
      dropDown,
      cadence,
      titleEdited,
      disableBtn,
      isFormValid,
      activeTab,
      organizationLevel,
      isStartable,
      isRestorable
    } = this.state;

    const vrsTotal = this.generateVrsTotal();
    const tableFields = Default.tableFields[1].fields.filter(
      (field) => field.visible
    );
    if (!isOpen) {
      return null;
    }
    if (!cadence.id)
      results = resultList.filter((result) =>
        cadence.workPackageIds.includes(result.id)
      );
    const allowEdit =
      cadence.id > 0
        ? allows('update', cadence) &&
          cadence.status !== CadenceStatuses.ACCEPTED
        : true;

    const resultIds = results.map((r) => r.id);
    return (
      <div
        className="detail-modal workpackage-detail cadence-detail"
        tabIndex="-1"
        role="dialog"
      >
        <div className="detail-dialog" role="document">
          <div className="detail-content">
            <div className="modal-header detail-header">
              <DetailsHeader
                results={results}
                cadence={cadence}
                allCadences={allCadences}
                onClose={this.closeForm}
                handleUpdate={this.handleFieldChange}
                handleDelete={this.props.deleteCadence}
                handleRestore={this.props.restoreCadence}
                vrsTotal={vrsTotal}
                isValid={isFormValid}
                titleEdited={titleEdited}
                isStartable={isStartable}
                isRestorable={isRestorable}
              />
            </div>
            <div className="modal-body cadence-modal-body">
              {/*        <ShortNameField
                shortName={cadence.short_name}
                onNameChange={this.handleFieldChange}
                allowEdit={allowEdit}
                isValid={isFormValid}
              /> */}
              <div
                className="goal-input-container"
                onClick={allowEdit ? this.openGoalEdit : null}
              >
                {this.renderGoalEditing()}
              </div>

              <ul className="nav nav-tabs" role="tablist">
                <li
                  role="presentation"
                  className={`${activeTab === 'plan' ? 'active' : ''}`}
                >
                  {this.renderTabLink('plan')}
                  {cadence.id ? <img src={divider} alt="" /> : null}
                </li>
                {cadence.id ? (
                  <li
                    role="presentation"
                    className={`${activeTab === 'scope' ? 'active' : ''}`}
                  >
                    {this.renderTabLink('scope')}
                  </li>
                ) : null}
              </ul>
              <div className="tab-content">
                {this.renderActiveTab(
                  dropDown,
                  resultIds,
                  results,
                  vrsTotal,
                  cadence,
                  currentUser,
                  allowEdit,
                  organizationLevel,
                  tableFields
                )}
              </div>
            </div>
            <div className="modal-footer">
              <div className="row footer-selects">
                {!cadence.id && (
                  <div className="col-md-3 buttons-container">
                    <div className="save-cancel-buttons">
                      <label>&nbsp;</label>
                      <button
                        type="button"
                        className="btn btn-md button-lavender"
                        disabled={disableBtn}
                        onClick={this.handleSave}
                      >
                        <strong>save</strong>
                      </button>
                      <button
                        type="button"
                        className="btn btn-md btn-default button-normal"
                        onClick={this.closeForm}
                      >
                        <strong>Cancel</strong>
                      </button>
                    </div>
                  </div>
                )}
                <div className="col-md-3">
                  {/*                  <SelectField
                    label="Team"
                    value={{ id: cadence.team_id }}
                    options={teamOptions}
                    allowEdit
                    handleChange={this.handleSelectChange}
                    placeholder="Select Team"
                    clearable={false}
                    labelClass=""
                    searchContainerClass=""
                  /> */}
                </div>
              </div>

              {cadence.id ? (
                <CadenceDetailStatus
                  cadence={cadence}
                  status={cadence.status}
                  allowEdit={allowEdit && isStartable}
                />
              ) : null}
            </div>
          </div>
        </div>
        {/*        {dropDown &&
          dropDown.id && (
            <DropDownMenu
              type={dropDown.type}
              dropDown={dropDown}
              key={dropDown.id}
            />
          )} */}
      </div>
    );
  }
}

CadenceDetail.propTypes = {
  id: PropTypes.number,
  cadence: PropTypes.object,
  results: PropTypes.array,
  allCadences: PropTypes.array,
  currentUser: PropTypes.object,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  updateCadence: PropTypes.func,
  deleteCadence: PropTypes.func,
  fetchTargetWorkPackages: PropTypes.func,
  addWorkPackageToCadence: PropTypes.func,
  removeWorkPackageFromCadence: PropTypes.func
};

const mapStateToProps = (state, ownProps) => {
  const cadenceWorkPackages = cadenceWorkPackagesSelector(state);
  const results = cadenceWorkPackages[ownProps.id] || [];
  const organizationLevels = currentOrganization().organization_levels;

  return {
    currentUser: state.userReducer.user,
    cadence: state.cadenceReducer.cadences.find((c) => c.id === ownProps.id),
    allCadences: state.cadenceReducer.cadences,
    cadenceList: state.cadenceReducer.cadences,
    scrumTeams: state.teamReducer.teams.filter((t) => t.kanban !== true),
    currentTeams: state.defaultOptionsReducer.currentTeams,
    resultList: state.workPackageReducer.stories,
    organizationLevels,
    results
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      openModal,
      createCadence,
      updateCadence,
      getAllCadences,
      deleteCadence,
      restoreCadence,
      fetchTargetWorkPackages,
      addWorkPackageToCadence,
      removeWorkPackageFromCadence,
      fetchUsers
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(CadenceDetail);
